'use client';

import { usePathname } from 'next/navigation';
import { type FC, useMemo } from 'react';
import { Skeleton } from '@/components/Skeleton';
import { TabNavigation } from '@/components/TabNavigation';
import { GameStaticCategories } from '@/helpers/game';
import type { Category } from '@/helpers/gameCategories';
import { useNavigation } from '@/hooks/navigation';
import { useCMSGameCategories } from '@/modules/lobby/hooks';

interface IGameCategoriesProps {
  subCategoryId?: string;
}

export const GameCategories: FC<IGameCategoriesProps> = ({ subCategoryId }) => {
  const pathname = usePathname();
  const { generateLink } = useNavigation();

  const { categories } = useCMSGameCategories();

  const currentPageUrl = useMemo(() => {
    if (subCategoryId) {
      return pathname.slice(0, pathname.indexOf('/sub-category'));
    }

    return pathname;
  }, [subCategoryId, pathname]);

  const urls = useMemo(
    () => categories.map(({ href }) => generateLink(href)),
    [categories, generateLink]
  );

  const selectedCategory = useMemo(
    () =>
      categories.findIndex(({ defaultCategoryId }, index) => {
        const url = urls[index];

        if (defaultCategoryId === GameStaticCategories.Lobby) {
          return url === currentPageUrl;
        }
        if (currentPageUrl === generateLink('/')) return false;
        return `${currentPageUrl}#gamesSection`.startsWith(url);
      }) || 0,
    [categories, currentPageUrl, generateLink]
  );

  const normalizedCategories = useMemo(
    (): Category[] =>
      categories.map(({ id, title, filterTitle, href }) => ({
        id,
        title: filterTitle || title,
        pathname: href,
      })),
    [categories]
  );

  return normalizedCategories.length ? (
    <TabNavigation
      fullScreen
      navItems={normalizedCategories}
      selectedItem={selectedCategory}
      withArrows
    />
  ) : (
    <Skeleton.Container padding="4px 0px">
      <Skeleton.Rect height={40} />
    </Skeleton.Container>
  );
};
